import type {Moment} from 'moment-timezone'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {OverlayTrigger} from '@freckle/educator-materials/ts/components/overlay-trigger'
import {Popover} from '@freckle/educator-materials/ts/components/popover'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import type {LicenseSubjectT} from '@freckle/school/ts/manage-teachers/models/license'
import {type LicenseAttrs} from '@freckle/school/ts/manage-teachers/models/license'
import {licenseCount, overutilized, utilizationText} from './license-panel.module.scss'

type Props = {
  subject: LicenseSubjectT
  license: LicenseAttrs | undefined | null
  school: SchoolAttrs
}

function getBodyForLicense(t: TranslateT, license: LicenseAttrs) {
  const subject = license.subject
  const licensesTotal = license.licensesTotal
  const licensesUtilized = license.licensesUtilized
  const asOf = license.licensesUtilizedAsOf

  if (
    licensesUtilized !== undefined &&
    licensesUtilized !== null &&
    asOf !== undefined &&
    asOf !== null
  ) {
    const percentUsed = Math.ceil((licensesUtilized / licensesTotal) * 100)

    return (
      <>
        <div className={licenseCount}>{licensesTotal}</div>
        <div className="panel-info">
          <UtilizationText percentUsed={percentUsed} asOf={asOf} />
        </div>
      </>
    )
  } else {
    const popover = (
      <Popover id={`license-popover-${subject}`}>
        <p className="no-margin">{t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_POPOVER')}</p>
      </Popover>
    )
    const popoverIcon = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={popover}>
        <i className="fa fa-question-circle hidden-print" />
      </OverlayTrigger>
    )

    return (
      <>
        <div className={licenseCount}>{licensesTotal}</div>
        <div className="panel-info">
          <p className="small">
            {t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_LICENSES_PURCHASED')}
          </p>
        </div>
        <div className="panel-info">
          <p className="small italic">
            <SafeTrans i18nKey="MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_USAGE_DATA_UNAVAILABLE" />{' '}
            {popoverIcon}
          </p>
        </div>
      </>
    )
  }
}

export default function LicensePanel(props: Props) {
  const {subject, license, school} = props
  const {t} = useSafeTranslation()

  const schoolName = school.name

  const emailSubject = t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_EMAIL_SUBJECT', {schoolName})

  const [body, emailCta] =
    license !== null && license !== undefined
      ? [getBodyForLicense(t, license), t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_GET_MORE')]
      : [
          <div className="panel-info" key="0">
            <p className="small muted center">
              {t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_NO_LICENSES_PURCHASED')}
            </p>
          </div>,
          t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_ADD_LICENSES'),
        ]

  return (
    <div className={`license-panel`} data-test={`dashboard-license-panel-${subject}`}>
      <div className="license-panel-header">
        {t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_SUBJECT_HEADER', {subject})}
      </div>
      <div className="license-panel-body">
        {body}
        <div className="small cta">
          <Link
            href={`mailto:freckle-support@renaissance.com?subject=${encodeURIComponent(emailSubject)}`}
            dataTest={`dashboard-license-panel-${subject}-emailcta-link`}
            reactRouter={false}
          >
            {emailCta}
          </Link>
        </div>
      </div>
    </div>
  )
}

type UtilizationTextProps = {
  percentUsed: number
  asOf: Moment
}

function UtilizationText(props: UtilizationTextProps) {
  const {percentUsed, asOf} = props
  const {t} = useSafeTranslation()

  const asOfDate = asOf.add(1, 'days').subtract(1, 'months').format('MMMM YYYY')

  const text =
    percentUsed > 105 ? (
      <span className={overutilized}>
        {t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_UTILIZATION_TEXT_OVER_LIMIT', {
          asOfDate,
        })}
      </span>
    ) : percentUsed > 95 ? (
      <span>
        <SafeTrans
          i18nKey="MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_UTILIZATION_TEXT_APPROACHING_LIMIT"
          values={{asOfDate}}
        />
      </span>
    ) : (
      <span>
        {t('MANAGE_TEACHERS_LICENSE_HEADER_LICENSE_PANEL_UTILIZATION_TEXT_WITHIN_LIMIT', {
          asOfDate,
        })}
      </span>
    )

  return <p className={utilizationText}>{text}</p>
}
